import { useLocation } from 'react-router-dom'
import i18n, { useCommonT } from '../../i18n'

/**
 * The localized notice above a page whose body stays English (Phase 3
 * localization, 3h):
 * - `legal`: /privacy and /terms. The English text is the binding one
 *   (common:legal.english_binding).
 * - `page`: /partners and /partners/attribution-kit, English-only this
 *   release by decision, Keith Q3 (common:page.english_only_note).
 *
 * Rendered ONCE, by the app shell (App.tsx, above the routes), not by those
 * pages. That is deliberate: the four pages are the only routes whose chunks
 * reach neither src/i18n/index.ts nor src/api/client.ts, and Rolldown groups
 * modules by the set of chunks that reach them. A note imported by the pages
 * themselves made them reach the shared i18n + API chunk, which regrouped
 * the eager chunks (React merged into it) and changed the modulepreload set
 * of every prerendered page (check:prerender-parity).
 *
 * Renders NOTHING while the interface is English, so English pages and the
 * prerendered HTML are unchanged. With VITE_I18N off the interface is always
 * English, so this is always null there.
 */
export type EnglishOnlyKind = 'legal' | 'page'

const KIND_BY_PATH: Record<string, EnglishOnlyKind> = {
  '/privacy': 'legal',
  '/terms': 'legal',
  '/partners': 'page',
  '/partners/attribution-kit': 'page',
}

/** Which notice a path gets, or null for every other route. */
export function englishOnlyKind(pathname: string): EnglishOnlyKind | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  return Object.hasOwn(KIND_BY_PATH, path) ? KIND_BY_PATH[path] : null
}

export default function EnglishOnlyNote() {
  const t = useCommonT()
  const { pathname } = useLocation()
  const kind = englishOnlyKind(pathname)
  if (!kind || i18n.language === 'en') return null
  return (
    <div className="max-w-read mx-auto px-4 pt-6">
      <p role="note" className="text-xs text-gray-400 bg-gray-900/50 border border-gray-800 rounded-lg px-3 py-2">
        {kind === 'legal' ? t('legal.english_binding') : t('page.english_only_note')}
      </p>
    </div>
  )
}
